import { memo, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { Form } from 'antd';
import type { FormInstance } from 'antd/es/form';

import { useAntMessage } from '@/shared/lib/hooks';
import { InputText, InputColorPicker } from '@/shared/ui/input';

import type { PatternFormValues } from '../../model/types/pattern-form';

import styles from './pattern-form.module.css';

interface PatternFormProps {
    data: PatternFormValues;
    form: FormInstance<PatternFormValues>;
    isLoading?: boolean;
    isSuccess?: boolean;
    mode: 'edit' | 'create';
    onSubmit: (values: PatternFormValues) => void;
}

export const PatternForm = memo((props: PatternFormProps) => {
    const {
        data = { pattern_name: '', color: '' },
        form,
        isLoading = false,
        isSuccess = false,
        mode,
        onSubmit,
    } = props;
    const { t } = useTranslation(['pattern', 'table']);
    const message = useAntMessage();

    const handleSubmit = useCallback(
        (values: PatternFormValues) => {
            onSubmit(values);
        },
        [onSubmit],
    );

    useEffect(() => {
        if (isSuccess) {
            message.success?.({
                content:
                    mode === 'edit'
                        ? t('pattern:updateSuccess')
                        : t('pattern:createSuccess'),
            });
        }
    }, [form, isSuccess, message, mode, t]);

    return (
        <Form<PatternFormValues>
            autoComplete="off"
            className={styles.root}
            form={form}
            initialValues={data}
            layout="horizontal"
            onFinish={handleSubmit}
            preserve
        >
            <Form.Item
                label={t('table:columns.pattern_name')}
                name="pattern_name"
                rules={[
                    {
                        required: true,
                        message: t('pattern:form.required'),
                    },
                ]}
            >
                <InputText
                    autoComplete="off"
                    disabled={isLoading}
                    isBlock
                />
            </Form.Item>

            <Form.Item
                label={t('table:columns.color')}
                name="color"
                rules={[
                    {
                        required: true,
                        message: t('pattern:form.required'),
                    },
                ]}
            >
                <InputColorPicker isDisabled={isLoading} />
            </Form.Item>
        </Form>
    );
});

PatternForm.displayName = 'PatternForm';
